import { useCallback, useEffect, useRef } from 'react';

import { EMapService } from '../../types/map';

const OPEN_TRANSPORT_ACCESSIBILITY_EVENT = 'map-services:open-transport-accessibility:v1';
const MAP_SERVICES_AVAILABLE_EVENT = 'map-services:available:v1';

/**
 * Возвращает функцию открытия сервиса транспортной доступности в микрофронте карты.
 *
 * Микрофронт загружается лениво при доскролле до секции инфраструктуры,
 * поэтому в момент клика он может быть еще не смонтирован и не слушать событие открытия.
 * В этом случае намерение запоминается, и событие открытия диспатчится после того,
 * как микрофронт сообщит о доступности сервисов через map-services:available:v1
 */
export function useOpenTransportAccessibility() {
  const isServiceAvailableRef = useRef(false);
  const isOpenPendingRef = useRef(false);

  useEffect(() => {
    const handleServicesAvailable = (event: Event) => {
      const { detail } = event as CustomEvent<EMapService[]>;
      const isAvailable = Array.isArray(detail) && detail.includes(EMapService.TransportAccessibility);

      isServiceAvailableRef.current = isAvailable;

      if (isAvailable && isOpenPendingRef.current) {
        isOpenPendingRef.current = false;

        // Микрофронт диспатчит map-services:available:v1 до подписки на событие открытия
        // (эффекты выполняются в порядке вызова хуков), поэтому событие откладывается в макротаску
        setTimeout(() => document.dispatchEvent(new CustomEvent(OPEN_TRANSPORT_ACCESSIBILITY_EVENT)), 0);
      }
    };

    document.addEventListener(MAP_SERVICES_AVAILABLE_EVENT, handleServicesAvailable);

    return () => document.removeEventListener(MAP_SERVICES_AVAILABLE_EVENT, handleServicesAvailable);
  }, []);

  return useCallback(() => {
    if (isServiceAvailableRef.current) {
      document.dispatchEvent(new CustomEvent(OPEN_TRANSPORT_ACCESSIBILITY_EVENT));
    } else {
      isOpenPendingRef.current = true;
    }
  }, []);
}
